export type CadrinoCopy = typeof srContent;

const srContent = {
  brand: 'Cadrino ERV',
  meta: {
    title: 'Cadrino ERV · ko je prisutan i ko sme gde',
    description:
      'Prisustvo, pristup i uređaji u jednom pregledu. Uvođenje ključ u ruke ili povezivanje na postojeću infrastrukturu.',
  },
  nav: {
    product: 'Pregled',
    how: 'Uvođenje',
    security: 'Sigurnost',
    integration: 'Integracije',
    contact: 'Kontakt',
    signIn: 'Prijava',
    bookDemo: 'Zakažite demo',
  },
  hero: {
    badge: 'Ključ u ruke · Ili na vašim uređajima',
    title: 'Evidencija koja ima smisla.',
    titleAccent: 'Pristup bez dileme.',
    subtitle:
      'Cadrino ERV povezuje uređaje za dolazak i pristup sa jednim jasnim pregledom za celu firmu. Vidite šta je stvarno zabeleženo, ko ima pravo pristupa i šta čeka obradu ako internet padne.',
    deploymentLine:
      'Nudimo kompletno rešenje sa hardverom, softverom i uređajima najnovije generacije (ključ u ruke). Ako već imate uređaje i infrastrukturu, uvodimo Cadrino ERV i po potrebi Cadrino HR da automatizujete ceo tok.',
    forWho:
      'Za kancelarije, IT firme, logistiku, maloprodaju, proizvodnju i sve kompanije koje žele tačne podatke bez Excela i nagađanja.',
    whyDifferent:
      'Dok drugi prodaju evidenciju radnog vremena, Cadrino ERV daje kontrolu nad prisustvom, pristupom i uređajima u svakodnevnom radu.',
    ctaPrimary: 'Zakažite demo',
    ctaSecondary: 'Pogledajte uvođenje',
    microTrust:
      'Demo na vašem scenariju · Besplatna obuka i podrška · 2 godine garancije na opremu',
    liveLabel: 'Sve pod kontrolom',
    liveDetail: '3 lokacije · 12 uređaja · bez ručnog proveravanja',
    preview: {
      windowTitle: 'Cadrino ERV · evidencija prisustva',
      badge: 'Pregled uživo',
      title: 'Evidencija prisustva',
      live: 'Uživo',
      tableTitle: 'Poslednji događaji',
      colTime: 'Vreme',
      colEmployee: 'Zaposleni',
      colEvent: 'Događaj',
      colLocation: 'Lokacija',
      ctaHint: 'Kliknite za interaktivni demo ispod ↓',
      metrics: [
        { label: 'Danas', value: '24', variant: 'success' as const },
        { label: 'Na lokaciji', value: '31', variant: 'info' as const },
        { label: 'Uređaji', value: '9/12', variant: 'neutral' as const },
      ],
      rows: [
        {
          key: '1',
          time: '12:08',
          name: 'Marko Jovanović',
          eventType: 'IN',
          eventLabel: 'Ulaz',
          location: 'Glavni ulaz',
        },
        {
          key: '2',
          time: '12:05',
          name: 'Ana Nikolić',
          eventType: 'OUT',
          eventLabel: 'Izlaz',
          location: 'Kancelarija',
        },
        {
          key: '3',
          time: '12:02',
          name: 'Stefan Đorđević',
          eventType: 'IN',
          eventLabel: 'Ulaz',
          location: 'Magacin',
        },
      ],
    },
  },
  problem: {
    title: 'Kad podaci ne znače isto svima',
    subtitle:
      'U mnogim firmama prisustvo, pristup i uređaji žive u više tabela, poruka i pretpostavki. Tada HR, operativa i direktor ne gledaju istu sliku.',
    cards: [
      {
        title: 'Previše ručnog rada',
        description:
          'Dolasci se proveravaju na više mesta, izveštaji se spajaju ručno, a tačan odgovor dolazi prekasno.',
      },
      {
        title: 'Pogrešan pristup na pogrešnom mestu',
        description:
          'Neko dobije pristup svuda, neko nigde, a promene ljudi i lokacija prave dodatni haos.',
      },
      {
        title: 'Problem se vidi tek kad zaboli',
        description:
          'Uređaj ne radi, događaj čeka obradu ili izveštaj kasni, a to se otkriva tek kada krenu pitanja oko sati ili pristupa.',
      },
    ],
    agitationTitle: 'Posledica nije tehnička, već poslovna',
    agitationIntro:
      'Kada podaci o prisustvu i pristupu nisu jasni, greška ne ostaje u sistemu. Ona završava u obračunu, kontroli i svakodnevnom radu.',
    agitationItems: [
      'HR zatvara mesec sporije jer spaja više izvora',
      'Menadžeri raspravljaju ko je bio prisutan, a ko nije',
      'Pogrešan pristup pravi bezbednosni i organizacioni problem',
      'Tim gubi poverenje u izveštaje i vraća se Excelu',
    ],
  },
  solution: {
    title: 'Jedan pregled za ljude, uređaje i pristup',
    subtitle:
      'Cadrino ERV povezuje uređaje u firmi sa pregledom koji koriste HR, operativa i rukovodstvo. Vidite ko je prisutan, ko sme gde i da li je sve prošlo kako treba.',
    pillars: [
      {
        title: 'Program ostaje u vašoj firmi',
        description:
          'Veza prema uređajima ide preko programa u vašoj mreži, a rad ne staje čim internet ili mreža naprave problem.',
      },
      {
        title: 'Jasan pregled bez crne kutije',
        description:
          'Vidite šta je obrađeno, šta čeka i gde je problem, umesto poruke koja ništa ne objašnjava.',
      },
      {
        title: 'Ono što je stvarno zabeleženo',
        description:
          'Uporedite stanje na uređaju sa pregledom pre obračuna i rešite razliku pre nego što napravi problem.',
      },
      {
        title: 'Više lokacija, jedan pregled',
        description:
          'Isti sistem za više objekata, ulaza i uređaja, bez razbacanih tabela i poruka.',
      },
    ],
    architecture: {
      terminal: 'Uređaji',
      worker: 'Program u firmi',
      cloud: 'Cadrino ERV',
      hr: 'Integracija',
    },
  },
  features: {
    title: 'Šta dobijate u svakodnevnom radu',
    subtitle: 'Bez velikih reči. Evo gde sistem pravi razliku.',
    labels: {
      problem: 'Šta vas muči',
      behavior: 'Šta Cadrino ERV radi',
      outcome: 'Šta dobijate',
    },
    items: [
      {
        title: 'Ko sme gde',
        problem: 'Ljudi dobijaju pristup preširoko ili napamet.',
        behavior:
          'Za svakog zaposlenog određujete na koje uređaje, ulaze ili lokacije ima pravo pristupa.',
        outcome: 'Manje grešaka, manje poziva podršci i jasnija kontrola.',
      },
      {
        title: 'Šta je stvarno zabeleženo',
        problem: 'U izveštaju piše jedno, na uređaju drugo.',
        behavior: 'Proveravate stanje na uređaju i upoređujete ga sa pregledom pre obračuna.',
        outcome: 'Tačniji sati i manje rasprava oko evidencije.',
      },
      {
        title: 'Kad internet padne',
        problem: 'Niko nije siguran da li su podaci sačuvani.',
        behavior:
          'Sistem čuva događaje i jasno pokazuje šta čeka obradu ili ponovno slanje.',
        outcome: 'Manje panike i manje ručnog unosa.',
      },
      {
        title: 'Da li uređaj radi',
        problem: 'Prekasno saznate da uređaj nije povezan.',
        behavior:
          'Status uređaja vidite odmah, pa reagujete pre nego što nastane rupa u evidenciji.',
        outcome: 'Manje praznina u podacima i manje iznenađenja.',
      },
      {
        title: 'Izveštaji za obračun',
        problem: 'HR spaja više tabela pre plate.',
        behavior: 'Izvezete pregled po periodu, lokaciji ili uređaju iz jednog mesta.',
        outcome: 'Brže zatvaranje meseca i jasniji podaci za obračun.',
      },
      {
        title: 'Integracija sa Cadrino HR',
        problem:
          'Podaci se ručno prenose između evidencije i HR sistema.',
        behavior:
          'Ako koristite Cadrino HR, zaposleni i odsustva mogu da se usklade sa Cadrino ERV-om.',
        outcome:
          'Manje duplog unosa kada firma koristi oba Cadrino proizvoda.',
      },
    ],
  },
  product: {
    title: 'Kako izgleda u praksi',
    subtitle:
      'Interaktivni demo: kliknite, pretražujte i posmatrajte kako se podaci ažuriraju u realnom vremenu. Bez prijave, sa simuliranim podacima.',
    tabs: {
      analytics: 'Analitika',
      attendance: 'Evidencija',
      devices: 'Uređaji',
    },
    windowTitles: {
      analytics: 'Cadrino ERV · analitika (demo)',
      attendance: 'Cadrino ERV · evidencija prisustva (demo)',
      devices: 'Cadrino ERV · uređaji (demo)',
    },
  },
  productDemo: {
    analytics: {
      badge: 'Interaktivni demo',
      title: 'Pregled za rukovodstvo i HR',
      subtitle: 'Brojke i grafikoni koji se osvežavaju dok sistem radi, ista slika koju tim koristi svakog dana.',
      live: 'Uživo',
      livePaused: 'Pauzirano',
      metrics: {
        todayPunches: 'Dolasci danas',
        todayPunchesSub: 'Ukupno u sistemu',
        onSite: 'Na lokaciji sada',
        onSiteSub: 'Procena po evidenciji',
        devicesOnline: 'Uređaji online',
        devicesSub: 'Povezani uređaji',
        activity: 'Poslednji sat',
        activitySub: 'Novi događaji',
      },
      chartHourlyTitle: 'Dolasci po satu (danas)',
      chartHourlySub: 'Vidljivo kada raste opterećenje tokom dana',
      chartHourlySeries: 'Dolasci',
      chartLocationTitle: 'Aktivnost po lokaciji',
      chartLocationSub: 'Gde se najviše koristi evidencija',
      chartLocationSeries: 'Dolasci',
    },
    devices: {
      badge: 'Interaktivni demo',
      title: 'Uređaji pod kontrolom',
      subtitle: 'Vidite koji uređaj radi, kada se poslednji put javio i gde je, bez poziva IT-u.',
      live: 'Uživo',
      livePaused: 'Pauzirano',
      metrics: {
        total: 'Ukupno uređaja',
        totalSub: 'U firmi',
        online: 'Online',
        onlineSub: 'Povezani sada',
        offline: 'Offline',
        offlineSub: 'Za proveru',
        uptime: 'Dostupnost',
        uptimeSub: 'Online od ukupnog broja',
      },
      tableTitle: 'Lista uređaja',
      tableSub: 'Status se menja u realnom vremenu, kao u pravom sistemu.',
      searchPlaceholder: 'Pretraga po nazivu ili lokaciji…',
      colDevice: 'Uređaj',
      colLocation: 'Lokacija',
      colStatus: 'Stanje',
      colLastSeen: 'Poslednja veza',
      statusOnline: 'Povezan',
      statusOffline: 'Nije povezan',
      noResults: 'Nema uređaja za izabrani filter.',
    },
    operations: {
      badge: 'Interaktivni demo',
      title: 'Od dodele do evidencije: jedan tok',
      subtitle:
        'Dodelite pristup, pošaljite na uređaj i vidite kako događaji stižu u evidenciju. Podaci su simulirani, bez prijave.',
      flowBanner: 'Sledeći korak: {step}',
      tabs: {
        assignments: '1. Dodele',
        sync: '2. Sinhronizacija',
        outcome: '3. Evidencija',
      },
      windowTitles: {
        assignments: 'Cadrino ERV · dodele (demo)',
        sync: 'Cadrino ERV · sinhronizacija (demo)',
        outcome: 'Cadrino ERV · evidencija (demo)',
      },
      assignments: {
        title: 'Ko sme na koji uređaj',
        subtitle: 'Izaberite uređaj, dodelite zaposlene i pripremite slanje na terminal.',
        deviceLabel: 'Uređaj',
        availableTitle: 'Dostupni',
        assignedTitle: 'Dodeljeni',
        assignButton: 'Dodeli izabrane',
        assignAndSync: 'Dodeli i pošalji na uređaj',
        selectHint: 'Označite zaposlene koje želite da dodelite.',
        noneSelected: 'Izaberite bar jednog zaposlenog.',
        assignedToast: 'Dodeljeno: {count} zaposlenih → {device}',
        live: 'Demo',
      },
      sync: {
        title: 'Sinhronizacija sa uređajem',
        subtitle: 'Vidite šta sistem radi posle dodele, bez nagađanja da li je prošlo.',
        live: 'Uživo',
        livePaused: 'Pauzirano',
        emptyHint: 'Prvo dodelite zaposlene u koraku „Dodele“, pa se ovde pojavljuje red čekanja.',
        colTask: 'Zadatak',
        colDevice: 'Uređaj',
        colStatus: 'Status',
        statusPending: 'Na čekanju',
        statusProcessing: 'U toku',
        statusDone: 'Završeno',
        statusFailed: 'Greška',
        pushUsers: 'Pošalji korisnike na uređaj',
        syncAccess: 'Sinhronizuj pravila pristupa',
        pullEvents: 'Povuci događaje sa uređaja',
        allDone: 'Sve je sinhronizovano, pogledajte evidenciju u sledećem koraku.',
        goToOutcome: 'Otvori evidenciju',
      },
      outcome: {
        banner: 'Događaji sa uređaja stižu u evidenciju sa jasnim statusom obrade.',
      },
      ctaDemo: 'Zakažite demo na vašem scenariju',
    },
  },
  deployment: {
    title: 'Dva načina da krenete',
    subtitle:
      'Biramo model uvođenja prema tome da li krećete od nule ili već imate uređaje i mrežu.',
    paths: [
      {
        title: 'Ključ u ruke',
        badge: 'Kompletno rešenje',
        description:
          'Uvodimo ceo sistem za vas: uređaje najnovije generacije, softver, podešavanje mreže i obuku tima. Vi dobijate spremno rešenje za prisustvo i pristup.',
        bullets: [
          'Hardver i softver iz jednog izvora',
          'Uređaji najnovije generacije za dolazak i pristup',
          '2 godine garancije na svu opremu koju uvodimo',
          'Uvođenje, puštanje u rad i primopredaja ključ u ruke',
          'Besplatna obuka i korisnička podrška nakon puštanja u rad',
          'Besplatna tehnička podrška nakon puštanja u rad',
          'Jasan pregled od prvog dana rada',
        ],
      },
      {
        title: 'Postojeća infrastruktura',
        badge: 'Softver na vašim uređajima',
        description:
          'Ako već imate uređaje i IT okruženje, uvodimo Cadrino ERV (i po potrebi Cadrino HR) na vašu postojeću opremu i automatizujemo tok podataka, bez zamene svega odjednom.',
        bullets: [
          'Povezivanje na postojeće uređaje i mrežu',
          'Automatizacija evidencije, pristupa i obrade',
          'Opciono usklađivanje sa Cadrino HR-om',
          'Ako uvodimo novu opremu: 2 godine garancije',
          'Besplatna obuka i korisnička podrška nakon puštanja u rad',
          'Besplatna tehnička podrška nakon puštanja u rad',
          'Postepeno širenje bez prekida rada',
        ],
      },
    ],
    note:
      'Na demo razgovoru procenjujemo šta vam stvarno treba, bez prodaje onoga što firma neće koristiti. Obuka zaposlenih, korisnička i tehnička podrška su besplatni, a oprema koju uvodimo ima 2 godine garancije.',
    cta: 'Zakažite demo',
  },
  howItWorks: {
    title: 'Kako radimo sa vama',
    subtitle: 'Jasan proces od procene do puštanja u rad, sa besplatnom obukom i podrškom.',
    steps: [
      {
        title: 'Procenjujemo vaš scenario',
        time: 'na demo razgovoru',
        description:
          'Mapiramo lokacije, uređaje, pravila pristupa i način rada timova da bismo predložili realan model uvođenja.',
      },
      {
        title: 'Predstavljamo plan unapređenja',
        time: 'pre početka radova',
        description:
          'Dobijate jasan plan koraka, rokova i očekivanih rezultata, bez nejasnih stavki i improvizacije.',
      },
      {
        title: 'Izvodimo radove i puštamo sistem u rad',
        time: 'po dogovorenom planu',
        description:
          'Implementiramo rešenje ključ u ruke ili na vašoj infrastrukturi, testiramo i predajemo operativan sistem.',
      },
      {
        title: 'Besplatna obuka i podrška nakon puštanja u rad',
        time: 'od prvog dana korišćenja',
        description:
          'Obuka korisnika i tehničkog tima je besplatna, kao i korisnička i tehnička podrška nakon puštanja u rad.',
      },
    ],
    rolloutChart: {
      title: 'Pregled faza uvođenja',
      subtitle:
        'Ilustracija tipičnog puta, tačan rokovnik i obim dogovaramo na demo razgovoru pre početka radova.',
      progressLabel: 'Spremnost sistema',
      footnote: 'Obuka i podrška nakon puštanja u rad uključeni su bez dodatnih troškova.',
      phases: [
        { shortLabel: 'Procena', value: 25 },
        { shortLabel: 'Plan', value: 50 },
        { shortLabel: 'Puštanje', value: 85 },
        { shortLabel: 'Podrška', value: 100 },
      ],
    },
  },
  useCases: {
    title: 'Radi u svakoj delatnosti',
    subtitle:
      'Nije ograničeno na određene branše. Ovo su tipični scenariji koje firme najčešće prepoznaju u svom radu.',
    items: [
      {
        industry: 'Jedna lokacija ili više lokacija',
        pain: 'Podaci su često rasuti po tabelama, porukama ili više nepovezanih sistema.',
        benefit:
          'Jedan pregled za prisustvo, pristup i status uređaja, bez obzira na tip delatnosti.',
      },
      {
        industry: 'Stabilan tim ili česte promene ljudi',
        pain: 'Promene zaposlenih, uloga i lokacija lako naprave greške u pristupu i evidenciji.',
        benefit:
          'Pravila se podešavaju jasno i brzo, pa svi rade po istim podacima.',
      },
      {
        industry: 'Smenski rad, kancelarijski rad ili kombinovano',
        pain: 'Različit tempo rada traži pouzdanu evidenciju i jasan tok obrade.',
        benefit:
          'Vidite šta je obrađeno, šta čeka i gde treba reagovati, bez nagađanja.',
      },
      {
        industry: 'Postojeći uređaji ili kompletno novo uvođenje',
        pain: 'Firma želi rešenje koje može da se uvede bez nepotrebnog rizika.',
        benefit:
          'Ključ u ruke ili na vašoj infrastrukturi, uz besplatnu obuku i podršku nakon puštanja u rad.',
      },
    ],
  },
  security: {
    title: 'Sigurnost bez kompromisa',
    subtitle:
      'Kontrola je u vašim rukama: jasno je gde podaci prolaze, ko šta menja i kako sistem radi kada mreža stane.',
    items: [
      {
        title: 'Podaci vaše firme ostaju strogo odvojeni',
        description:
          'Podaci jedne kompanije nikada se ne mešaju sa drugima, a pristup je jasno organizovan po firmi i ulozi.',
      },
      {
        title: 'Uređaji ostaju u vašoj mreži',
        description:
          'Veza prema uređajima ide preko programa u firmi, umesto da uređaje izlažete direktno internetu.',
      },
      {
        title: 'Svaka važna izmena je vidljiva',
        description:
          'Pregled aktivnosti pokazuje ko je i kada menjao pristup, zaposlenog ili obradu podataka.',
      },
      {
        title: 'Ništa se ne gubi kada veza stane',
        description:
          'Događaji čekaju obradu i ostaju vidljivi dok se veza ne vrati, bez ručnog spašavanja podataka.',
      },
    ],
  },
  integrationSection: {
    title: 'Cadrino ERV + Cadrino HR',
    subtitle:
      'ERV vodi prisustvo, pristup i uređaje. Cadrino HR vodi zaposlene, ugovore, odsustva i dokumentaciju. Povezani, rade kao jedan operativni tok bez ručnog prepisivanja.',
    choices: [
      {
        title: 'Cadrino ERV',
        description:
          'Kontrola prisustva, pristupa i uređaja u realnom vremenu, fokus ovog sajta.',
        current: true,
      },
      {
        title: 'Zajedno sa Cadrino HR',
        description:
          'Automatska razmena ljudi, odsustava i evidencije između operativnog i HR sistema.',
      },
    ],
    syncTitle: 'Šta rešavate kada koristite oba sistema?',
    syncItems: [
      'Zaposleni i struktura ulaze bez duplog unosa, nema “prepisivanja” iz HR-a',
      'Odobrena odsustva se automatski poštuju u evidenciji, bez korekcija i rasprava',
      'Evidencija sa uređaja ide dalje u HR i pripremu obračuna, na istoj, proverljivoj bazi',
    ],
    note:
      'Dobijate jedan tok podataka: manje administracije, manje grešaka i brži obračun. Počinjete sa ERV-om, a povezivanje sa Cadrino HR aktivirate kada vama odgovara (bez prekida rada).',
    currentBadge: 'Cadrino ERV · ovaj sajt',
    ctaDemo: 'Zakažite demo i plan povezivanja',
  },
  cta: {
    title: 'Hoćete da vidite kako bi ovo radilo kod vas?',
    subtitle:
      'Pokažemo vaš scenario: ključ u ruke ili postojeće uređaje, pravila pristupa i kako bi tim radio bez Excela i nagađanja.',
    primary: 'Zakažite demo',
    secondary: 'Pogledajte uvođenje',
  },
  footer: {
    tagline: 'Jasan pregled prisustva, pristupa i uređaja u celoj firmi.',
    sales: 'contact@cadrinoerv.com',
    rights: 'Sva prava zadržana.',
    ecosystem: 'Deo Cadrino porodice proizvoda.',
    cadrinoHrLabel: 'Cadrino HR',
  },
  trustStrip: {
    items: [
      { title: 'Uređaji u vašoj mreži', description: 'Veza ostaje pod kontrolom firme' },
      { title: 'Jedan operativni pregled', description: 'Prisustvo, pristup i status uređaja' },
      { title: 'Podrška i obuka bez doplate', description: 'Korisnička i tehnička podrška + obuka zaposlenih' },
      { title: '2 godine garancije', description: 'Na svu opremu koju uvodimo u ključ u ruke modelu' },
    ],
  },
  faq: {
    title: 'Česta pitanja',
    items: [
      {
        q: 'Da li nudite kompletno rešenje (ključ u ruke)?',
        a: 'Da. Možemo da uvedemo hardver, softver i uređaje najnovije generacije, sa punim uvođenjem i primopredajom ključ u ruke. Pogodno je kada još nemate sistem ili želite sve na jednom mestu.',
      },
      {
        q: 'Šta ako već imamo uređaje?',
        a: 'Uvodimo Cadrino ERV na vašu postojeću infrastrukturu i automatizujemo evidenciju, pristup i obradu. Po potrebi povezujemo i Cadrino HR. Nije obavezna zamena opreme od prvog dana.',
      },
      {
        q: 'Postoji li integracija sa Cadrino HR?',
        a: 'Da. Ako koristite Cadrino HR, sistemi mogu da razmenjuju zaposlene, odsustva i evidenciju. Cadrino ERV radi i bez te integracije.',
      },
      {
        q: 'Šta ako internet ili mreža naprave problem?',
        a: 'Sistem čuva događaje i jasno pokazuje šta je obrađeno, a šta čeka obradu ili ponovno slanje. Tim vidi status bez nagađanja.',
      },
      {
        q: 'Koliko traje uvođenje?',
        a: 'Zavisi od broja lokacija i uređaja. Na demo razgovoru prolazimo vaš scenario i dajemo realan plan koraka, bez obećanja koje ne možemo da ispunimo.',
      },
      {
        q: 'Da li su obuka i podrška uključene?',
        a: 'Da. Nakon puštanja u rad obuka je potpuno besplatna, a tehnička i korisnička podrška su takođe besplatne.',
      },
      {
        q: 'Da li možemo da radimo paralelno sa postojećim rešenjem?',
        a: 'Da. Mnoge firme prvo uvedu Cadrino ERV uz postojeći način rada, pa postepeno prebacuju pregled i procese kada tim stekne poverenje u podatke.',
      },
      {
        q: 'Ko u firmi koristi sistem?',
        a: 'HR, operativa, rukovodstvo i IT, svako gleda isti pregled, sa ulogama koje odgovaraju njihovom poslu.',
      },
      {
        q: 'Kako se određuje cena?',
        a: 'Prema obimu: broj zaposlenih, lokacija i uređaja. Tačan predlog dobijate na demo razgovoru, prilagođenom vašoj firmi.',
      },
    ],
  },
  attendanceSimulation: {
    sectionTitle: 'Isprobajte ERV uživo, bez prijave',
    sectionSubtitle:
      'Interaktivni demo evidencije prisustva. Podaci su simulirani; osvežavanje stranice pokreće novu sesiju. Novi dolazak stiže na svakih 4–8 sekundi, kao u pravom sistemu.',
    frameIntro:
      'Kliknite, pretražujte i posmatrajte kako se brojači i tabela ažuriraju u realnom vremenu. Ovo je isti tip pregleda koji tim koristi svakog dana.',
    windowTitle: 'Cadrino ERV · evidencija prisustva (demo)',
    ariaRegion: 'Interaktivni demo evidencije prisustva',
    demoBadge: 'Interaktivni demo',
    pageTitle: 'Evidencija prisustva',
    pageSubtitle: 'Pregled i upravljanje zabeleženim dolascima i odlascima',
    liveStatus: 'Uživo',
    livePaused: 'Pauzirano (tab u pozadini)',
    sessionNote:
      'Demo podaci za ovu sesiju. Osvežite stranicu za novi set zapisa.',
    metrics: {
      totalRecords: 'Ukupno zapisa',
      allTime: 'U ovoj demo sesiji',
      todayRecords: 'Danas',
      todaySubtitle: 'Današnja evidencija',
      thisWeek: 'Ove nedelje',
      weekSubtitle: 'Poslednjih 7 dana',
      thisMonth: 'Ovog meseca',
      monthSubtitle: 'Poslednjih 30 dana',
      activeUsers: 'Aktivni korisnici',
      uniqueEmployees: 'Jedinstveni zaposleni',
    },
    filtersTitle: 'Filteri',
    filtersHint: 'Pretraga filtrira zapise u tabeli. Widgeti prate celu demo sesiju.',
    searchLabel: 'Pretraga',
    searchPlaceholder: 'Zaposleni, uređaj, lokacija…',
    eventTypeLabel: 'Tip događaja',
    allEvents: 'Svi događaji',
    dateRangeLabel: 'Period',
    dateToday: 'Danas',
    dateAll: 'Sva sesija',
    actionsLabel: 'Akcije',
    refresh: 'Osveži',
    exportCsv: 'Izvezi CSV',
    exportDisabledTitle: 'Dostupno u punom sistemu',
    exportDisabledMessage:
      'Export je dostupan u punoj verziji Cadrino ERV nakon prijave. Zakažite demo da vidite ceo tok.',
    exportDialog: {
      title: 'Export u punom Cadrino ERV sistemu',
      description:
        'U demo pregledu export nije aktivan. U punoj verziji, nakon prijave, izvezete evidenciju u CSV za HR i obračun, sa filterima koje već vidite ovde.',
      close: 'Razumem',
      cta: 'Zakažite demo',
    },
    recordsTitle: 'Evidencija ({count})',
    tableDescription:
      'Prikazano najviše {visible} najnovijih zapisa u demo pregledu.',
    colTime: 'Vreme',
    colEmployee: 'Zaposleni',
    colEvent: 'Događaj',
    colDevice: 'Uređaj',
    colLocation: 'Lokacija',
    noResults: 'Nema zapisa za izabrane filtere.',
    pageSizeNote: 'Demo prikazuje 5 najnovijih zapisa. U punom sistemu birate veličinu stranice i straničite kroz celu istoriju.',
  },
  mock: {
    dashboard: {
      windowTitle: 'Cadrino ERV · pregled',
      overviewLabel: 'Jedan pregled',
      summary: '12 uređaja · 3 lokacije · 1 sistem',
      live: 'Sve pod kontrolom',
    },
    devices: {
      cols: ['Uređaj', 'Lokacija', 'Stanje', 'Poslednja veza'],
      online: 'Povezan',
      offline: 'Nije povezan',
    },
    assignments: {
      cols: ['Zaposleni', 'Glavni ulaz', 'Kancelarija', 'Magacin'],
    },
    queue: {
      cols: ['Događaj', 'Uređaj', 'Stanje', 'Akcija'],
      status: {
        synced: 'Obrađeno',
        pending: 'Čeka',
        failed: 'Greška',
      },
      retry: 'Pošalji ponovo',
      dash: '—',
    },
  },
};

const enContent: CadrinoCopy = {
  brand: 'Cadrino ERV',
  meta: {
    title: 'Cadrino ERV · who is present and who may go where',
    description:
      'Attendance, access, and devices in one view. Turnkey rollout or integration with your existing infrastructure.',
  },
  nav: {
    product: 'Overview',
    how: 'Rollout',
    security: 'Security',
    integration: 'Integrations',
    contact: 'Contact',
    signIn: 'Sign in',
    bookDemo: 'Book a demo',
  },
  hero: {
    badge: 'Turnkey · Or on your existing devices',
    title: 'Attendance that makes sense.',
    titleAccent: 'Access without guesswork.',
    subtitle:
      'Cadrino ERV connects attendance and access devices to one clear view for the whole company. See what was really recorded, who has access, and what is waiting to process if the network drops.',
    deploymentLine:
      'We offer full rollout, hardware, software, and latest-generation devices (turnkey). If you already have devices and infrastructure, we deploy Cadrino ERV and, when needed, Cadrino HR to automate the full flow.',
    forWho:
      'For offices, IT firms, logistics, retail, manufacturing, and any company that needs accurate data without spreadsheets and guesswork.',
    whyDifferent:
      'While others sell time attendance software, Cadrino ERV gives control over attendance, access, and devices in everyday operations.',
    ctaPrimary: 'Book a demo',
    ctaSecondary: 'See rollout',
    microTrust:
      'A demo around your scenario · Free training and support · 2-year warranty on delivered equipment',
    liveLabel: 'Everything under control',
    liveDetail: '3 sites · 12 devices · no manual checking',
    preview: {
      windowTitle: 'Cadrino ERV · attendance',
      badge: 'Live view',
      title: 'Attendance',
      live: 'Live',
      tableTitle: 'Latest events',
      colTime: 'Time',
      colEmployee: 'Employee',
      colEvent: 'Event',
      colLocation: 'Location',
      ctaHint: 'Click for the interactive demo below ↓',
      metrics: [
        { label: 'Today', value: '24', variant: 'success' as const },
        { label: 'On site', value: '31', variant: 'info' as const },
        { label: 'Devices', value: '9/12', variant: 'neutral' as const },
      ],
      rows: [
        {
          key: '1',
          time: '12:08 PM',
          name: 'James Mitchell',
          eventType: 'IN',
          eventLabel: 'In',
          location: 'Main entrance',
        },
        {
          key: '2',
          time: '12:05 PM',
          name: 'Sarah Collins',
          eventType: 'OUT',
          eventLabel: 'Out',
          location: 'Office',
        },
        {
          key: '3',
          time: '12:02 PM',
          name: 'David Brooks',
          eventType: 'IN',
          eventLabel: 'In',
          location: 'Warehouse',
        },
      ],
    },
  },
  problem: {
    title: 'When data means something different to everyone',
    subtitle:
      'In many companies, attendance, access, and devices live across spreadsheets, messages, and assumptions. Then HR, operations, and leadership are not looking at the same picture.',
    cards: [
      {
        title: 'Too much manual work',
        description:
          'Attendance is checked in several places, reports are merged by hand, and the correct answer comes too late.',
      },
      {
        title: 'Wrong access in the wrong place',
        description:
          'Someone gets access everywhere, someone nowhere, and people or location changes create extra chaos.',
      },
      {
        title: 'You only see the issue when it hurts',
        description:
          'A device stops working, an event waits to process, or a report arrives late, and you discover it only when questions start about hours or access.',
      },
    ],
    agitationTitle: 'The consequence is business, not technical',
    agitationIntro:
      'When attendance and access data are unclear, the error does not stay inside the system. It reaches payroll, controls, and daily operations.',
    agitationItems: [
      'HR closes the month more slowly because data comes from several sources',
      'Managers argue about who was present and who was not',
      'Wrong access creates a security and organizational problem',
      'Teams lose trust in reports and go back to spreadsheets',
    ],
  },
  solution: {
    title: 'One view for people, devices, and access',
    subtitle:
      'Cadrino ERV connects the devices in your company to one view used by HR, operations, and leadership. You see who is present, who may go where, and whether everything went through correctly.',
    pillars: [
      {
        title: 'The on-site program stays in your company',
        description:
          'The connection to devices runs through software in your network, so work does not stop the moment the internet or network has a problem.',
      },
      {
        title: 'A clear view instead of a black box',
        description:
          'You see what has been processed, what is waiting, and where the issue is, instead of a status message that explains nothing.',
      },
      {
        title: 'What was actually recorded',
        description:
          'Compare the device state with the system before payroll and fix the difference before it becomes a problem.',
      },
      {
        title: 'Multiple sites, one view',
        description:
          'The same system across locations, entrances, and devices, without scattered spreadsheets.',
      },
    ],
    architecture: {
      terminal: 'Devices',
      worker: 'On-site program',
      cloud: 'Cadrino ERV',
      hr: 'Integration',
    },
  },
  features: {
    title: 'What you get in everyday work',
    subtitle: 'No big words. Here is where the system makes a difference.',
    labels: {
      problem: 'What hurts',
      behavior: 'What Cadrino ERV does',
      outcome: 'What you get',
    },
    items: [
      {
        title: 'Who may go where',
        problem: 'People get access too broadly or by guesswork.',
        behavior:
          'For each employee, you define which devices, entrances, or locations they may use.',
        outcome: 'Fewer mistakes, fewer support calls, and clearer control.',
      },
      {
        title: 'What was actually recorded',
        problem: 'The report says one thing, the device another.',
        behavior:
          'You check the device state and compare it with the system before payroll.',
        outcome: 'More accurate hours and fewer disputes around records.',
      },
      {
        title: 'When the internet drops',
        problem: 'Nobody is sure whether the data was saved.',
        behavior:
          'The system keeps events and clearly shows what is waiting to process or resend.',
        outcome: 'Less panic and less manual entry.',
      },
      {
        title: 'Whether a device is working',
        problem: 'You find out too late that the device is offline.',
        behavior:
          'You see device status right away and react before a gap appears in the records.',
        outcome: 'Fewer data gaps and fewer surprises.',
      },
      {
        title: 'Reports for payroll',
        problem: 'HR merges several tables before payroll.',
        behavior:
          'You export one view by period, location, or device from a single place.',
        outcome: 'Faster month-end close and clearer payroll input.',
      },
      {
        title: 'Integration with Cadrino HR',
        problem:
          'Data is copied manually between attendance and HR systems.',
        behavior:
          'If you use Cadrino HR, employees and absences can stay aligned with Cadrino ERV.',
        outcome:
          'Less duplicate entry when your company uses both Cadrino products.',
      },
    ],
  },
  product: {
    title: 'What it looks like in practice',
    subtitle:
      'Interactive demo, click, search, and watch data update in real time. No sign-in, simulated data.',
    tabs: {
      analytics: 'Analytics',
      attendance: 'Attendance',
      devices: 'Devices',
    },
    windowTitles: {
      analytics: 'Cadrino ERV · analytics (demo)',
      attendance: 'Cadrino ERV · attendance (demo)',
      devices: 'Cadrino ERV · devices (demo)',
    },
  },
  productDemo: {
    analytics: {
      badge: 'Interactive demo',
      title: 'View for leadership and HR',
      subtitle: 'Numbers and charts that refresh as the system runs, the same picture your team uses every day.',
      live: 'Live',
      livePaused: 'Paused',
      metrics: {
        todayPunches: "Today's punches",
        todayPunchesSub: 'Total in the system',
        onSite: 'On site now',
        onSiteSub: 'Estimated from attendance',
        devicesOnline: 'Devices online',
        devicesSub: 'Connected now',
        activity: 'Last hour',
        activitySub: 'New events',
      },
      chartHourlyTitle: 'Arrivals by hour (today)',
      chartHourlySub: 'See when load increases during the day',
      chartHourlySeries: 'Arrivals',
      chartLocationTitle: 'Activity by location',
      chartLocationSub: 'Where attendance is used most',
      chartLocationSeries: 'Arrivals',
    },
    devices: {
      badge: 'Interactive demo',
      title: 'Devices under control',
      subtitle: 'See which device works, when it last checked in, and where it is, without calling IT.',
      live: 'Live',
      livePaused: 'Paused',
      metrics: {
        total: 'Total devices',
        totalSub: 'In the company',
        online: 'Online',
        onlineSub: 'Connected now',
        offline: 'Offline',
        offlineSub: 'Needs attention',
        uptime: 'Availability',
        uptimeSub: 'Online share of total',
      },
      tableTitle: 'Device list',
      tableSub: 'Status changes in real time, like the real system.',
      searchPlaceholder: 'Search by name or location…',
      colDevice: 'Device',
      colLocation: 'Location',
      colStatus: 'Status',
      colLastSeen: 'Last seen',
      statusOnline: 'Connected',
      statusOffline: 'Not connected',
      noResults: 'No devices match the filter.',
    },
    operations: {
      badge: 'Interactive demo',
      title: 'From assignment to attendance, one flow',
      subtitle:
        'Assign access, push to the device, and see events arrive in attendance. Simulated data, no sign-in.',
      flowBanner: 'Next step: {step}',
      tabs: {
        assignments: '1. Assignments',
        sync: '2. Sync',
        outcome: '3. Attendance',
      },
      windowTitles: {
        assignments: 'Cadrino ERV · assignments (demo)',
        sync: 'Cadrino ERV · sync (demo)',
        outcome: 'Cadrino ERV · attendance (demo)',
      },
      assignments: {
        title: 'Who can use which device',
        subtitle: 'Pick a device, assign employees, and prepare the push to the terminal.',
        deviceLabel: 'Device',
        availableTitle: 'Available',
        assignedTitle: 'Assigned',
        assignButton: 'Assign selected',
        assignAndSync: 'Assign and push to device',
        selectHint: 'Select employees to assign.',
        noneSelected: 'Select at least one employee.',
        assignedToast: 'Assigned: {count} employees → {device}',
        live: 'Demo',
      },
      sync: {
        title: 'Device synchronization',
        subtitle: 'See what the system does after assignment, no guessing if it worked.',
        live: 'Live',
        livePaused: 'Paused',
        emptyHint: 'Assign employees in step 1 first, the queue appears here.',
        colTask: 'Task',
        colDevice: 'Device',
        colStatus: 'Status',
        statusPending: 'Pending',
        statusProcessing: 'In progress',
        statusDone: 'Done',
        statusFailed: 'Failed',
        pushUsers: 'Push users to device',
        syncAccess: 'Sync access rules',
        pullEvents: 'Pull events from device',
        allDone: 'Everything is synced, check attendance in the next step.',
        goToOutcome: 'Open attendance',
      },
      outcome: {
        banner: 'Device events arrive in attendance with a clear processing status.',
      },
      ctaDemo: 'Book a demo for your scenario',
    },
  },
  deployment: {
    title: 'Two ways to get started',
    subtitle:
      'We choose the rollout model based on whether you are starting from scratch or already have devices and a network.',
    paths: [
      {
        title: 'Turnkey',
        badge: 'Complete solution',
        description:
          'We roll out the full system for you: latest-generation devices, software, network setup, and team onboarding. You get attendance and access ready to use.',
        bullets: [
          'Hardware and software from one source',
          'Latest-generation attendance and access devices',
          '2-year warranty on all equipment we deliver',
          'Rollout, go-live, and turnkey handover',
          'Free user training and customer support after go-live',
          'Free technical support after go-live',
          'A clear operational view from day one',
        ],
      },
      {
        title: 'Existing infrastructure',
        badge: 'Software on your devices',
        description:
          'If you already have devices and IT in place, we deploy Cadrino ERV (and Cadrino HR when needed) on your equipment and automate the data flow, without replacing everything at once.',
        bullets: [
          'Connect to your existing devices and network',
          'Automate attendance, access, and processing',
          'Optional alignment with Cadrino HR',
          'If we introduce new equipment: 2-year warranty',
          'Free user training and customer support after go-live',
          'Free technical support after go-live',
          'Expand step by step without disrupting operations',
        ],
      },
    ],
    note:
      'In a demo we assess what you truly need, without selling what your company will not use. Employee training, customer support, and technical support are free after go-live, and delivered equipment includes a 2-year warranty.',
    cta: 'Book a demo',
  },
  howItWorks: {
    title: 'How we work with you',
    subtitle: 'A clear process from assessment to go-live, with free training and support.',
    steps: [
      {
        title: 'We assess your scenario',
        time: 'in a demo call',
        description:
          'We map locations, devices, access rules, and team workflows so the rollout model matches your real operations.',
      },
      {
        title: 'We present an improvement plan',
        time: 'before work starts',
        description:
          'You get a clear step-by-step plan, timeline, and expected outcomes without vague promises.',
      },
      {
        title: 'We execute and go live',
        time: 'according to the agreed plan',
        description:
          'We implement turnkey or on your existing infrastructure, test the setup, and hand over an operational system.',
      },
      {
        title: 'Free training and support after go-live',
        time: 'from day one of use',
        description:
          'Training for users and technical staff is fully free, and both customer and technical support are also free after go-live.',
      },
    ],
    rolloutChart: {
      title: 'Rollout phase overview',
      subtitle:
        'Typical path illustration, exact timeline and scope are agreed in your demo call before work starts.',
      progressLabel: 'System readiness',
      footnote: 'Training and post go-live support are included at no extra cost.',
      phases: [
        { shortLabel: 'Assessment', value: 25 },
        { shortLabel: 'Plan', value: 50 },
        { shortLabel: 'Go-live', value: 85 },
        { shortLabel: 'Support', value: 100 },
      ],
    },
  },
  useCases: {
    title: 'Works across all industries',
    subtitle:
      'Not limited to specific verticals. These are common scenarios companies recognize in day-to-day operations.',
    items: [
      {
        industry: 'Single-site or multi-site operations',
        pain: 'Data is often split across spreadsheets, messages, or disconnected tools.',
        benefit:
          'One view for attendance, access, and device status, regardless of industry.',
      },
      {
        industry: 'Stable teams or frequent staff changes',
        pain: 'Changes in people, roles, and locations easily create access and attendance errors.',
        benefit:
          'Rules stay clear and fast to apply, so everyone works from the same data.',
      },
      {
        industry: 'Shift-based, office-based, or mixed work',
        pain: 'Different work rhythms require reliable records and a clear processing flow.',
        benefit:
          'You see what is processed, what is pending, and where action is needed, without guesswork.',
      },
      {
        industry: 'Existing devices or a complete new rollout',
        pain: 'The company needs a rollout path without unnecessary risk.',
        benefit:
          'Turnkey or on your infrastructure, with free training and support after go-live.',
      },
    ],
  },
  security: {
    title: 'Security without compromise',
    subtitle:
      'Control stays in your hands: you always know where data moves, who changed what, and how the system behaves when the network stops.',
    items: [
      {
        title: 'Your company data remains strictly isolated',
        description:
          'One company’s data is never mixed with another company’s, and access is clearly organized by company and role.',
      },
      {
        title: 'Devices stay inside your network',
        description:
          'The connection to devices runs through software in your company instead of exposing devices directly to the internet.',
      },
      {
        title: 'Every critical change is traceable',
        description:
          'The activity view shows who changed access, employee records, or processing steps and when it happened.',
      },
      {
        title: 'Nothing is lost when the connection stops',
        description:
          'Events stay queued and visible until connectivity returns, without manual data rescue.',
      },
    ],
  },
  integrationSection: {
    title: 'Cadrino ERV + Cadrino HR',
    subtitle:
      'ERV runs attendance, access, and devices. Cadrino HR runs people records, contracts, absences, and HR documentation. Connected together, they work as one operational flow without manual re-entry.',
    choices: [
      {
        title: 'Cadrino ERV',
        description:
          'Real-time control of attendance, access, and devices, the focus of this site.',
        current: true,
      },
      {
        title: 'With Cadrino HR',
        description:
          'Automatic exchange of people, absences, and attendance between operations and HR.',
      },
    ],
    syncTitle: 'What you fix when both systems work together',
    syncItems: [
      'People and team structure sync without duplicate entry, no re-typing from HR',
      'HR-approved absences are automatically reflected in attendance, fewer corrections and disputes',
      'Device attendance flows into HR and payroll prep, the same verified data',
    ],
    note:
      'One end-to-end data flow: less admin, fewer errors, and faster payroll decisions. Start with ERV, then enable the HR connection when it fits your rollout (no disruption).',
    currentBadge: 'Cadrino ERV · this site',
    ctaDemo: 'Book a demo and integration plan',
  },
  cta: {
    title: 'Want to see how this would work for you?',
    subtitle:
      'We walk through your scenario: turnkey or existing devices, access rules, and how the team would work without spreadsheets and guesswork.',
    primary: 'Book a demo',
    secondary: 'See rollout',
  },
  footer: {
    tagline: 'A clear view of attendance, access, and devices across the company.',
    sales: 'contact@cadrinoerv.com',
    rights: 'All rights reserved.',
    ecosystem: 'Part of the Cadrino product family.',
    cadrinoHrLabel: 'Cadrino HR',
  },
  trustStrip: {
    items: [
      { title: 'Devices on your network', description: 'Connections stay under company control' },
      { title: 'One operational view', description: 'Attendance, access, and device status' },
      { title: 'Free support and training', description: 'Customer support, technical support, and employee training' },
      { title: '2-year warranty', description: 'On all equipment we deliver in turnkey projects' },
    ],
  },
  faq: {
    title: 'Frequently asked questions',
    items: [
      {
        q: 'Do you offer a complete turnkey solution?',
        a: 'Yes. We can supply hardware, software, and latest-generation devices with full rollout and turnkey handover. It fits when you have no system yet or want everything from one partner.',
      },
      {
        q: 'What if we already have devices?',
        a: 'We deploy Cadrino ERV on your existing infrastructure and automate attendance, access, and processing. Cadrino HR can be connected when needed. Replacing all equipment on day one is not required.',
      },
      {
        q: 'Is there an integration with Cadrino HR?',
        a: 'Yes. If you use Cadrino HR, the systems can exchange employees, absences, and attendance. Cadrino ERV also works without that integration.',
      },
      {
        q: 'What if the internet or network has issues?',
        a: 'The system keeps events and clearly shows what was processed and what is waiting to be sent or retried. Your team sees status without guesswork.',
      },
      {
        q: 'How long does rollout take?',
        a: 'It depends on locations and devices. On a demo we walk through your scenario and give a realistic step-by-step plan, no promises we cannot keep.',
      },
      {
        q: 'Are training and support included?',
        a: 'Yes. After go-live, training is fully free, and both technical and customer support are also free.',
      },
      {
        q: 'Can we run in parallel with what we use today?',
        a: 'Yes. Many companies introduce Cadrino ERV alongside their current process, then shift the daily view when the team trusts the data.',
      },
      {
        q: 'Who in the company uses the system?',
        a: 'HR, operations, leadership, and IT, everyone works from the same view, with roles that match their job.',
      },
      {
        q: 'How is pricing determined?',
        a: 'By scope: employees, locations, and devices. You get a tailored proposal on a demo call.',
      },
    ],
  },
  attendanceSimulation: {
    sectionTitle: 'Try ERV live, no sign-in required',
    sectionSubtitle:
      'Interactive attendance demo. Data is simulated; refreshing the page starts a new session. A new record arrives every 4–8 seconds, just like the real system.',
    frameIntro:
      'Click, search, and watch counters and the table update in real time. This is the same type of view your team uses every day.',
    windowTitle: 'Cadrino ERV · attendance (demo)',
    ariaRegion: 'Interactive attendance demo',
    demoBadge: 'Interactive demo',
    pageTitle: 'Attendance Management',
    pageSubtitle: 'View and manage employee attendance records',
    liveStatus: 'Live',
    livePaused: 'Paused (tab in background)',
    sessionNote: 'Demo data for this session. Refresh the page for a new set of records.',
    metrics: {
      totalRecords: 'Total records',
      allTime: 'In this demo session',
      todayRecords: "Today's records",
      todaySubtitle: "Today's attendance",
      thisWeek: 'This week',
      weekSubtitle: 'Last 7 days',
      thisMonth: 'This month',
      monthSubtitle: 'Last 30 days',
      activeUsers: 'Active users',
      uniqueEmployees: 'Unique employees',
    },
    filtersTitle: 'Filters',
    filtersHint: 'Search filters the table. Widgets reflect the full demo session.',
    searchLabel: 'Search',
    searchPlaceholder: 'Employee, device, location…',
    eventTypeLabel: 'Event type',
    allEvents: 'All events',
    dateRangeLabel: 'Date range',
    dateToday: 'Today',
    dateAll: 'Full session',
    actionsLabel: 'Actions',
    refresh: 'Refresh',
    exportCsv: 'Export CSV',
    exportDisabledTitle: 'Available in the full system',
    exportDisabledMessage:
      'Export is available in the full Cadrino ERV after sign-in. Book a demo to see the complete flow.',
    exportDialog: {
      title: 'Export in the full Cadrino ERV system',
      description:
        'Export is not active in this demo view. In the full product, after sign-in, you export attendance to CSV for HR and payroll, with the same filters you see here.',
      close: 'Got it',
      cta: 'Book a demo',
    },
    recordsTitle: 'Attendance records ({count})',
    tableDescription: 'Showing up to {visible} newest records in the demo view.',
    colTime: 'Time',
    colEmployee: 'Employee',
    colEvent: 'Event',
    colDevice: 'Device',
    colLocation: 'Location',
    noResults: 'No records match the selected filters.',
    pageSizeNote:
      'The demo shows the 5 newest records. In the full system you choose page size and browse full history.',
  },
  mock: {
    dashboard: {
      windowTitle: 'Cadrino ERV · overview',
      overviewLabel: 'One view',
      summary: '12 devices · 3 sites · 1 system',
      live: 'Everything under control',
    },
    devices: {
      cols: ['Device', 'Location', 'Status', 'Last seen'],
      online: 'Connected',
      offline: 'Not connected',
    },
    assignments: {
      cols: ['Employee', 'Main entrance', 'Office', 'Warehouse'],
    },
    queue: {
      cols: ['Event', 'Device', 'Status', 'Action'],
      status: {
        synced: 'Processed',
        pending: 'Waiting',
        failed: 'Error',
      },
      retry: 'Send again',
      dash: '—',
    },
  },
};

export const cadrinoCopy = { sr: srContent, en: enContent };

export function getCadrinoCopy(language: string): CadrinoCopy {
  return cadrinoCopy[language === 'en' ? 'en' : 'sr'];
}
